// Today's Focus Component
// Shows actionable commitments due today with Done/Skip actions

import { useState } from 'react';
import { Check, X, Clock, Calendar, ChevronDown, ChevronUp, Flame } from 'lucide-react';
import type { Commitment } from '../types';
import MicButton from './MicButton';

interface TodaysFocusProps {
  commitments: Commitment[];
  onCheckIn: (id: number, completed: boolean, notes?: string) => Promise<void>;
  onUncheckIn?: (id: number) => Promise<void>; // For undoing a check-in
  onComplete?: (id: number) => Promise<void>; // For one-time commitments
  onUncomplete?: (id: number) => Promise<void>;
  loading?: boolean;
  compact?: boolean; // For Dashboard view
  hideWhenEmpty?: boolean;
}

interface TodayItem {
  commitment: Commitment;
  isCompletedToday: boolean;
}

// Helper: Get start of current week (Monday)
function getWeekStart(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when Sunday
  return new Date(d.setDate(diff));
}

// Helper: Get check-ins count for current week
function getWeekCheckInsCount(commitment: Commitment): number {
  const weekStart = getWeekStart(new Date());
  const weekStartStr = weekStart.toISOString().split('T')[0];
  
  return commitment.check_ins?.filter(ci => {
    return ci.completed && ci.date >= weekStartStr;
  }).length || 0;
}

// Helper: Get check-ins count for current month
function getMonthCheckInsCount(commitment: Commitment): number {
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const monthStartStr = monthStart.toISOString().split('T')[0];
  
  return commitment.check_ins?.filter(ci => {
    return ci.completed && ci.date >= monthStartStr;
  }).length || 0;
}

// Helper: Get commitments that are due today (includes completed items with flag)
export function getTodaysCommitments(commitments: Commitment[]): TodayItem[] {
  const today = new Date();
  const dayOfWeek = (today.getDay() + 6) % 7; // Convert to Mon=0
  const todayStr = today.toISOString().split('T')[0];
  const dayOfMonth = today.getDate();

  const result: TodayItem[] = [];

  for (const c of commitments) {
    // Only include active commitments (or completed one-time for today)
    if (c.status !== 'pending' && c.status !== 'in_progress') {
      // For one-time commitments that were completed today, still show them
      if (c.frequency === 'one_time' && c.status === 'completed') {
        const completedToday = c.completed_at && 
          new Date(c.completed_at).toISOString().split('T')[0] === todayStr;
        if (completedToday) {
          result.push({ commitment: c, isCompletedToday: true });
        }
      }
      continue;
    }

    // Check if already checked in today
    const hasCheckedInToday = c.check_ins?.some(ci => ci.date === todayStr && ci.completed);

    let isDueToday = false;

    switch (c.frequency) {
      case 'daily':
        isDueToday = true;
        break;
        
      case 'weekly':
        // Handle flexible scheduling mode
        if (c.scheduling_mode === 'flexible' && c.target_count > 1) {
          // Show until target is met for the week (or if already checked in today)
          const weekCheckIns = getWeekCheckInsCount(c);
          isDueToday = weekCheckIns < c.target_count || hasCheckedInToday;
        } else if (c.scheduling_mode === 'specific' && c.preferred_days?.length > 0) {
          // Handle specific days mode with preferred_days array
          isDueToday = c.preferred_days.includes(dayOfWeek);
        } else if (c.preferred_day_of_week !== null && c.preferred_day_of_week !== undefined) {
          // Fallback to old preferred_day_of_week for backwards compatibility
          isDueToday = c.preferred_day_of_week === dayOfWeek;
        } else {
          // Default: show weekly commitments on the day they were created
          const createdDate = new Date(c.created_at);
          isDueToday = (createdDate.getDay() + 6) % 7 === dayOfWeek;
        }
        break;
        
      case 'monthly':
        // Handle flexible scheduling mode
        if (c.scheduling_mode === 'flexible' && c.target_count > 1) {
          // Show until target is met for the month (or if already checked in today)
          const monthCheckIns = getMonthCheckInsCount(c);
          isDueToday = monthCheckIns < c.target_count || hasCheckedInToday;
        } else {
          // First of month or preferred day
          isDueToday = dayOfMonth === 1;
        }
        break;
        
      case 'one_time':
        // Due today
        isDueToday = c.due_date === todayStr;
        break;
    }

    if (isDueToday) {
      result.push({
        commitment: c,
        isCompletedToday: hasCheckedInToday || false
      });
    }
  }

  // Sort: uncompleted items first, then completed
  result.sort((a, b) => {
    if (a.isCompletedToday === b.isCompletedToday) {
      return a.commitment.description.localeCompare(b.commitment.description);
    }
    return a.isCompletedToday ? 1 : -1;
  });

  return result;
}

// Format time for display
function formatTime(timeStr: string | null): string {
  if (!timeStr) return '';
  try {
    const [hours, minutes] = timeStr.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  } catch {
    return '';
  }
}

// Get frequency label
function getFrequencyLabel(frequency: Commitment['frequency']): string {
  switch (frequency) {
    case 'daily': return 'Daily';
    case 'weekly': return 'Weekly';
    case 'monthly': return 'Monthly';
    case 'one_time': return 'One-time';
    default: return '';
  }
}

export default function TodaysFocus({ 
  commitments, 
  onCheckIn,
  onUncheckIn,
  onComplete,
  onUncomplete,
  loading = false,
  compact = false,
  hideWhenEmpty = false,
}: TodaysFocusProps) {
  const [expanded, setExpanded] = useState(true);
  const [processingId, setProcessingId] = useState<number | null>(null);
  const [skipModalId, setSkipModalId] = useState<number | null>(null);
  const [skipReason, setSkipReason] = useState('');

  const todaysItems = getTodaysCommitments(commitments);
  
  // Count completed from the new structure
  const completedToday = todaysItems.filter(item => item.isCompletedToday).length;
  const totalDueToday = todaysItems.length;
  void (totalDueToday - completedToday); // pendingToday calculated but reserved for future use

  const handleDone = async (commitment: Commitment) => {
    setProcessingId(commitment.id);
    try {
      // For one-time commitments, mark as complete (moves to Completed tab)
      // For recurring commitments, create a check-in
      if (commitment.frequency === 'one_time' && onComplete) {
        await onComplete(commitment.id);
      } else {
        await onCheckIn(commitment.id, true);
      }
    } finally {
      setProcessingId(null);
    }
  };

  const handleSkip = async (id: number) => {
    setProcessingId(id);
    try {
      await onCheckIn(id, false, skipReason);
      setSkipModalId(null);
      setSkipReason('');
    } finally {
      setProcessingId(null);
    }
  };

  const handleUndo = async (id: number) => {
    const item = todaysItems.find((row) => row.commitment.id === id);
    const isOneTimeComplete =
      item?.commitment.frequency === 'one_time'
      && item.commitment.status === 'completed';
    if (isOneTimeComplete && !onUncomplete) return;
    if (!isOneTimeComplete && !onUncheckIn) return;
    setProcessingId(id);
    try {
      if (isOneTimeComplete && onUncomplete) {
        await onUncomplete(id);
      } else if (onUncheckIn) {
        await onUncheckIn(id);
      }
    } finally {
      setProcessingId(null);
    }
  };

  // Today's date formatted
  const today = new Date();
  const dateDisplay = today.toLocaleDateString('en-US', { 
    weekday: 'short', 
    month: 'long', 
    day: 'numeric' 
  });

  if (hideWhenEmpty && !loading && totalDueToday === 0) {
    return null;
  }

  if (loading) {
    if (hideWhenEmpty) return null;
    return (
      <div className="bg-white rounded-xl shadow-sm p-5">
        <div className="animate-pulse">
          <div className="h-5 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-12 bg-gray-100 rounded"></div>
            <div className="h-12 bg-gray-100 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      {/* Header */}
      <div 
        className={`flex items-center justify-between p-4 ${compact ? 'cursor-pointer hover:bg-gray-50' : ''} border-b border-gray-100`}
        onClick={compact ? () => setExpanded(!expanded) : undefined}
        role={compact ? "button" : undefined}
        aria-expanded={compact ? expanded : undefined}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-navy to-navy-light flex items-center justify-center">
            <Calendar className="text-white" size={18} />
          </div>
          <div>
            <h3 className="font-semibold text-navy">Today's Focus</h3>
            <p className="text-sm text-gray-500">{dateDisplay}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {totalDueToday > 0 && (
            <span className={`text-sm font-medium px-2.5 py-1 rounded-full ${
              completedToday === totalDueToday 
                ? 'bg-green-100 text-green-700' 
                : 'bg-navy/10 text-navy'
            }`}>
              {completedToday} of {totalDueToday}
            </span>
          )}
          {compact && (
            expanded ? <ChevronUp size={20} className="text-gray-400" /> : <ChevronDown size={20} className="text-gray-400" />
          )}
        </div>
      </div>

      {/* Content */}
      {(!compact || expanded) && (
        <div className="divide-y divide-gray-50">
          {totalDueToday === 0 ? (
            <p className="px-4 py-3 text-sm text-gray-500 text-center">
              No Grow Goals due today — enjoy your day!
            </p>
          ) : (
            todaysItems.map((item) => (
              <div 
                key={item.commitment.id} 
                className={`p-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-3 transition-colors ${
                  item.isCompletedToday 
                    ? 'bg-green-50/50' 
                    : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-3 flex-1 min-w-36">
                  {/* Icon: green checkmark for completed, clock for pending */}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    item.isCompletedToday 
                      ? 'bg-green-100' 
                      : 'bg-gray-100'
                  }`}>
                    {item.isCompletedToday ? (
                      <Check className="text-green-600" size={16} />
                    ) : (
                      <Clock className="text-gray-400" size={16} />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={`font-medium truncate ${
                      item.isCompletedToday ? 'text-gray-600' : 'text-gray-900'
                    }`}>
                      {item.commitment.description}
                    </p>
                    <div className="flex items-center gap-2 text-sm text-gray-500 flex-wrap">
                      {item.commitment.preferred_time && (
                        <span>{formatTime(item.commitment.preferred_time)}</span>
                      )}
                      <span className="text-gray-300">•</span>
                      {/* Show period progress for flexible scheduling */}
                      {item.commitment.scheduling_mode === 'flexible' && item.commitment.target_count > 1 && item.commitment.period_progress ? (
                        <span className={`font-medium ${
                          item.commitment.period_progress.is_period_complete 
                            ? 'text-green-600' 
                            : 'text-navy'
                        }`}>
                          {item.commitment.period_progress.period_completed}/{item.commitment.period_progress.period_target} {item.commitment.period_progress.period_label}
                        </span>
                      ) : (
                        <span>{getFrequencyLabel(item.commitment.frequency)}</span>
                      )}
                      {/* Show specific days for weekly */}
                      {item.commitment.scheduling_mode === 'specific' && item.commitment.preferred_days?.length > 1 && (
                        <>
                          <span className="text-gray-300">•</span>
                          <span className="text-xs">
                            {item.commitment.preferred_days.map(d => ['M', 'T', 'W', 'T', 'F', 'S', 'S'][d]).join('')}
                          </span>
                        </>
                      )}
                      {item.commitment.streak !== null && item.commitment.streak > 0 && (
                        <>
                          <span className="text-gray-300">•</span>
                          <span className="flex items-center gap-1 text-orange-500">
                            <Flame size={14} />
                            {item.commitment.streak}
                          </span>
                        </>
                      )}
                      {/* Show completed badge */}
                      {item.isCompletedToday && (
                        <>
                          <span className="text-gray-300">•</span>
                          <span className="text-green-600 font-medium">Done</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
                  {item.isCompletedToday ? (
                    /* Undo button for completed items */
                    <button
                      onClick={() => handleUndo(item.commitment.id)}
                      disabled={
                        processingId === item.commitment.id
                        || (
                          item.commitment.frequency === 'one_time'
                          && item.commitment.status === 'completed'
                            ? !onUncomplete
                            : !onUncheckIn
                        )
                      }
                      className="px-3 py-1.5 pointer-coarse:py-2.5 bg-gray-100 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-200 disabled:opacity-50 flex items-center gap-1.5 transition-colors"
                    >
                      <X size={14} />
                      Undo
                    </button>
                  ) : (
                    /* Done/Skip buttons for pending items */
                    <>
                      <button
                        onClick={() => handleDone(item.commitment)}
                        disabled={processingId === item.commitment.id}
                        className="px-3 py-1.5 pointer-coarse:py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-1.5 transition-colors"
                      >
                        <Check size={14} />
                        Done
                      </button>
                      <button
                        onClick={() => setSkipModalId(item.commitment.id)}
                        disabled={processingId === item.commitment.id}
                        className="px-3 py-1.5 pointer-coarse:py-2.5 bg-gray-100 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-200 disabled:opacity-50 flex items-center gap-1.5 transition-colors"
                      >
                        <X size={14} />
                        Skip
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Skip reason modal */}
      {skipModalId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 transition-opacity duration-200" role="dialog" aria-modal="true">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6 transition-all duration-200">
            <h3 className="text-lg font-semibold text-navy mb-4">Skip Today?</h3>
            <p className="text-sm text-gray-600 mb-4">
              That's okay! Would you like to note why? (Optional)
            </p>
            <div className="relative">
              <textarea
                value={skipReason}
                onChange={(e) => setSkipReason(e.target.value)}
                placeholder="e.g., Traveling today, not feeling well..."
                className="w-full p-3 pr-12 border border-gray-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy"
                rows={3}
              />
              <MicButton
                value={skipReason}
                onChange={setSkipReason}
                className="absolute bottom-2 right-2"
              />
            </div>
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => {
                  setSkipModalId(null);
                  setSkipReason('');
                }}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-600 font-medium rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleSkip(skipModalId)}
                disabled={processingId === skipModalId}
                className="flex-1 px-4 py-2 bg-navy text-white font-medium rounded-lg hover:bg-navy-dark disabled:opacity-50 transition-colors"
              >
                {processingId === skipModalId ? 'Skipping...' : 'Skip Today'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
